<!-- Render different kinds of OverlayOptionFields.

More special fields often have their own component file and should be
extracted from here once used e.g. inside widget components to be able
to reuse them.

-->

<template>
  <v-form
    ref="form"
    @submit.prevent
  >
    <!-- Optional warning message -->
    <v-layout
      v-if="warning"
      align-center
      class="mb-2"
    >
      <v-icon
        color="warning"
        class="mr-2"
      >
        error_outline
      </v-icon>
      <span class="warning--text">
        {{ warning.prefix }}: <b>{{ warning.fieldLabel }}</b>
      </span>
    </v-layout>
    <!-- HeaderField -->
    <div v-if="isHeaderField(field)">
      <div class="mt-3 title mb-2">
        {{ label }}
      </div>
      <v-divider class="mb-4" />
    </div>
    <!-- TextField (single input or multiline textarea) -->
    <overlay-data-text-field
      v-if="isTextField(field)"
      :field="field"
      :value.sync="value"
      class="mb-3"
    />
    <!-- ColorField rendered as a custom menu widget -->
    <overlay-data-color-field
      v-if="isColorField(field)"
      :field="field"
      :value.sync="value"
      :color-palette="colorPalette"
      :render-key="colorfieldRenderKey"
      class="mb-3"
    />
    <!-- DateField rendered as a custom menu widget -->
    <overlay-data-date-field
      v-if="isDateField(field)"
      :field="field"
      :value.sync="value"
      :date-format="field.data.date_format || 'DD.MM.YYYY'"
      class="mb-3"
    />
    <!-- ImageField rendered as a label, a preview image and some buttons -->
    <overlay-data-image-field
      v-if="isImageField(field)"
      :field="field"
      :value.sync="value"
      class="mb-3"
      @update:value="$emit('changed')"
      @change-image="$emit('change-image', $event)"
    />
    <!-- BooleanField -->
    <overlay-data-boolean-field
      v-if="isBooleanField(field)"
      :field="field"
      :value.sync="value"
      class="mb-3"
    />
    <!-- ChoiceField -->
    <div
      v-if="isChoiceField(field)"
      class="px-3 pt-3 pb-2 mb-3 overlay-options-field-bg"
    >
      <!-- Radiogroup -->
      <div v-if="field.data.ui && field.data.ui === 'radiogroup'">
        <div
          v-if="label"
          class="mb-1"
        >
          <span class="field-label">{{ label }}</span>
        </div>
        <v-radio-group
          v-model="value"
          :rules="field.rules"
          :column="shouldRenderChoiceRadioGroupAsColumn"
          :style="field.data.css ? field.data.css.field : null"
          :required="field.required"
          class="mt-2"
          data-cy="overlay-data-choicefield"
        >
          <v-radio
            v-for="(choice, choiceIndex) in choices"
            :key="field.name + '_choice_' + choiceIndex"
            :label="choice.label !== undefined ? choice.label : choice"
            :value="choice.value !== undefined ? choice.value : choice"
            color="msr"
          />
        </v-radio-group>
      </div>
      <!-- Dropdown -->
      <v-select
        v-else
        v-model="value"
        :items="choices"
        :rules="field.rules"
        item-value="value"
        item-text="label"
        :label="label"
        :placeholder="placeholder"
        :required="field.required"
        :style="field.data.css ? field.data.css.field : null"
        data-cy="overlay-data-choicefield"
      />
    </div>
    <!-- TextAlignmentField -->
    <overlay-data-text-alignment-field
      v-if="isTextAlignmentField(field)"
      :field="field"
      :value.sync="value"
    />
    <!-- NumberField -->
    <v-text-field
      v-if="isNumberField(field)"
      v-model="value"
      class="pt-4 px-3 pb-2 mt-0 mb-3 overlay-options-field-bg"
      :type="numberFieldInputType"
      :label="label"
      :placeholder="placeholder"
      :required="field.required"
      :rules="field.rules"
      :style="field.data.css ? field.data.css.field : null"
      data-cy="overlay-data-numberfield"
      @focus="onInputFocused"
    />
    <!-- PriceField -->
    <overlay-data-price-field
      v-if="isPriceField(field)"
      :field="field"
      :value.sync="value"
    />
    <!-- RangeField -->
    <overlay-data-range-field
      v-if="isRangeField(field)"
      :field="field"
      :value.sync="value"
      class="mb-3"
    />
  </v-form>
</template>

<script>
import {transformPriceValueToUseNumberOfDecimals} from '@/utils';

import {
  ensureDotSeparator,
  getNumDecimals,
  overlayFieldIdentifierMethodsMixin,
  OverlayOptionsField,
  OverlayOptionsFieldWarning,
} from '@/mixins/overlayOptions.js';

import OverlayDataBooleanField from '@/components/OverlayDataBooleanField';
import OverlayDataColorField from '@/components/OverlayDataColorField';
import OverlayDataDateField from '@/components/OverlayDataDateField';
import OverlayDataImageField from '@/components/OverlayDataImageField';
import OverlayDataPriceField from '@/components/OverlayDataPriceField';
import OverlayDataRangeField from '@/components/OverlayDataRangeField';
import OverlayDataTextAlignmentField from '@/components/OverlayDataTextAlignmentField';
import OverlayDataTextField from '@/components/OverlayDataTextField';

export default {
  components: {
    OverlayDataBooleanField,
    OverlayDataColorField,
    OverlayDataDateField,
    OverlayDataImageField,
    OverlayDataPriceField,
    OverlayDataRangeField,
    OverlayDataTextAlignmentField,
    OverlayDataTextField,
  },
  mixins: [overlayFieldIdentifierMethodsMixin],
  props: {
    field: {
      type: OverlayOptionsField,
      required: true,
    },
    /**
     * Optional warning object shown above the interface.
     */
    warning: {
      type: OverlayOptionsFieldWarning,
      default: null,
    },
    colorPalette: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      colorfieldRenderKey: 0,
      rangefieldAsNumberInput: false,
      value: this.field.value,
    };
  },
  computed: {
    /**
     * IE11/Edge do not support using a comma separator in number inputs.
     *
     * The only workaround for this is to use type="text" instead.
     * We'll lose the increase/decrease buttons, but all rule
     * validations will still work and german users can use a comma as
     * they are used to.
     */
    numberFieldInputType() {
      if (this.$store.state.common.runtimeEnv.edge) {
        return 'text';
      }
      return 'number';
    },
    name() {
      return this.field.name;
    },
    schema() {
      return this.field.schema;
    },
    data() {
      return this.field.data;
    },
    label() {
      return this.data.label;
    },
    placeholder() {
      return this.data.placeholder;
    },
    choices() {
      return this.data.choices || [];
    },
    shouldRenderChoiceRadioGroupAsColumn() {
      if (
        (this.field.data.column === undefined && this.choices.length > 3) ||
        this.$vuetify.breakpoint.xs
      ) {
        return true;
      }
      return !!this.field.data.column;
    },
  },
  watch: {
    value: function (val) {
      this.$emit('changed', val);
    },
  },
  created() {
    /**
     * Allow overwriting the internal value from outside, e.g. when an
     * image URL needs to be replaced with one that was just uploaded
     * or when we need to populate UI from TVSpot.template_data initially.
     */
    this.$bus.$on('set-overlay-data-field-value', (fieldName, newValue) => {
      if (this.name !== fieldName) {
        return;
      }
      if (this.isPriceField(this.field)) {
        const numDecimals = getNumDecimals(
          this.field.schema.multipleOf || 0.01
        );
        newValue = transformPriceValueToUseNumberOfDecimals(
          newValue,
          numDecimals
        );
      }
      this.value = newValue;
      if (this.isColorField(this.field)) {
        // Force ColorPicker to rerender so the displayed color matches.
        this.$nextTick(() => {
          this.colorfieldRenderKey += 1;
        });
      }
    });
  },
  methods: {
    onInputFocused(event) {
      if (this.field && this.field.data && this.field.data.select_on_focus) {
        event.target.select();
      }
    },
    validate() {
      return this.$refs.form.validate();
    },
    /**
     * Return name and value of field as an object.
     */
    getData() {
      let value = this.value;

      if (this.isBooleanField(this.field)) {
        value = !!value;
      } else if (
        this.isNumberField(this.field) ||
        this.isPriceField(this.field) ||
        this.isRangeField(this.field)
      ) {
        value = ensureDotSeparator(value);
        value = Number(value);
      } else if (
        this.isImageField(this.field) ||
        this.isTextField(this.field)
      ) {
        if (value === null) {
          // null would fail jsonschema validation on the backend, an
          // empty string will pass it and is the preferred value
          // anyway.
          value = '';
        }
      }

      const data = {
        label: this.label,
        name: this.name,
        value: value,
      };
      return data;
    },
  },
};
</script>

<style scoped>
.field-label {
  font-size: 12px;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.54);
}
</style>



// WEBPACK FOOTER //
// src/components/OverlayDataField.vue